import { EnrollmentPlanCSIDWithUXControls } from "@/api/types";
import { EnrollmentContext } from "@/state";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

export const useSelectedEnrollmentPlan = () => {
  const [state] = useContext(EnrollmentContext);
  const [enrollmentPlanCsid, setEnrollmentPlanCsid] = useState<EnrollmentPlanCSIDWithUXControls>(undefined);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const planFromParams = searchParams.get("enrollmentPlan");
    if (planFromParams) {
      const plans = state.experienceData.plan_csids;
      const selectedPlan = plans.find((ep) => ep.id === +planFromParams);
      setEnrollmentPlanCsid(selectedPlan);
    }
  }, [searchParams, state.experienceData]);

  const customerServicePhoneNumber = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "customer_service_phone_number")?.value;
  }, [enrollmentPlanCsid]);

  const hideDrugPricing = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_drug_pricing" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hideMailPharmacyOnFBPTool = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_mail_pharmacy_on_fbp_tool" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hideMedicarePrescriptionPaymentPlanPage = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_medicare_prescription_payment_plan" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hidePharmacySearch = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_pharmacy_search" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hidePlanDocuments = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_plan_documents" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hideFormularyLookup = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_formulary_lookup" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const hideOnlinePharmacyOnFBPTool = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return !!uxControls.find((c) => c.control === "hide_online_pharmacy_on_fbp_tool" && c.value === "true");
  }, [enrollmentPlanCsid]);

  const mailPharmacyIdentifier = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "mail_pharmacy_identifier")?.value;
  }, [enrollmentPlanCsid]);

  const medicarePrescriptionPaymentPlanPhoneSection = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "medicare_prescription_payment_plan_phone_section")?.value;
  }, [enrollmentPlanCsid]);

  const medicarePrescriptionPaymentPlanOnlineSection = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "medicare_prescription_payment_plan_online_section")?.value;
  }, [enrollmentPlanCsid]);

  const medicarePrescriptionPaymentPlanMailSection = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "medicare_prescription_payment_plan_mail_section")?.value;
  }, [enrollmentPlanCsid]);

  const medicarePrescriptionPaymentPlanEmailSection = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "medicare_prescription_payment_plan_email_section")?.value;
  }, [enrollmentPlanCsid]);

  const medicarePrescriptionPaymentPlanExplanationSection = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "medicare_prescription_payment_plan_explanation_section")?.value;
  }, [enrollmentPlanCsid]);

  const onlinePharmacyIdentifier = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "online_pharmacy_identifier")?.value;
  }, [enrollmentPlanCsid]);

  const overrideFormularyLookupURL = useMemo(() => {
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    return uxControls.find((c) => c.control === "override_formulary_lookup_url")?.value;
  }, [enrollmentPlanCsid]);

  return {
    enrollmentPlanCsid,
    customerServicePhoneNumber,
    hideDrugPricing,
    hideMailPharmacyOnFBPTool,
    hideMedicarePrescriptionPaymentPlanPage,
    hideOnlinePharmacyOnFBPTool,
    hidePharmacySearch,
    hidePlanDocuments,
    hideFormularyLookup,
    mailPharmacyIdentifier,
    medicarePrescriptionPaymentPlanPhoneSection,
    medicarePrescriptionPaymentPlanOnlineSection,
    medicarePrescriptionPaymentPlanEmailSection,
    medicarePrescriptionPaymentPlanMailSection,
    medicarePrescriptionPaymentPlanExplanationSection,
    onlinePharmacyIdentifier,
    overrideFormularyLookupURL,
  };
};
